import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { submitB2B } from "../lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const B2B_HERO = "https://images.unsplash.com/photo-1577106263724-2c8e03bfe9cf?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const B2B = () => {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", company: "", email: "", phone: "", business_type: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await submitB2B(form);
      toast.success(t.b2b.sent);
      setForm({ name: "", company: "", email: "", phone: "", business_type: "", message: "" });
    } catch (err) {
      toast.error("Error: " + (err.response?.data?.detail || err.message));
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="b2b-page" className="pt-32">
      {/* HERO */}
      <section className="container-pad pb-20">
        <div className="eyebrow text-[#F97316]">{t.b2b.eyebrow}</div>
        <h1 className="display-serif mt-6 text-5xl sm:text-7xl lg:text-[7rem] leading-[0.95] tracking-tight whitespace-pre-line max-w-5xl">
          {t.b2b.title}
        </h1>
        <p className="mt-10 max-w-3xl text-xl sm:text-2xl text-foreground/75 font-light leading-[1.55]">
          {t.b2b.sub}
        </p>
      </section>

      <section className="container-pad">
        <div className="aspect-[16/7] overflow-hidden">
          <img src={B2B_HERO} alt="Professional kitchen" className="w-full h-full object-cover" />
        </div>
      </section>

      {/* AVAILABLE FORMATS */}
      <section className="border-t border-border mt-28" data-testid="formats-section">
        <div className="container-pad py-24 sm:py-32">
          <div className="eyebrow text-[#F97316]">{t.b2b.formatsTitle}</div>
          <p className="mt-6 max-w-2xl text-base sm:text-lg font-light leading-[1.6] text-foreground/70">
            {t.b2b.formatsIntro}
          </p>

          <div className="mt-16 space-y-16 sm:space-y-20">
            {t.b2b.formats.map((cat) => (
              <div key={cat.id} className="border-t border-foreground/15 pt-10" data-testid={`format-${cat.id}`}>
                <h3 className="display-serif text-4xl sm:text-5xl lg:text-6xl">{cat.name}</h3>

                {/* Format rows — same visual language as the original retail/horeca pair */}
                <div className={`mt-10 grid ${cat.rows.length > 1 ? "md:grid-cols-2" : "md:grid-cols-1"} gap-10 lg:gap-16 max-w-4xl`}>
                  {cat.rows.map((r, idx) => (
                    <div key={idx} className="border-t border-foreground/15 pt-6" data-testid={`format-${cat.id}-row-${idx}`}>
                      <div className="eyebrow text-foreground/55">{r.label}</div>
                      <div className="display-serif text-2xl sm:text-3xl lg:text-4xl mt-3">{r.value}</div>
                    </div>
                  ))}
                </div>

                {/* Products list */}
                <div className="mt-12 grid md:grid-cols-2 gap-10 lg:gap-16 max-w-4xl">
                  <div>
                    <div className="eyebrow text-foreground/55 mb-4">{cat.productsLabel}</div>
                    <ul className="space-y-2.5">
                      {cat.products.map((p) => (
                        <li key={p} className="display-serif text-xl sm:text-2xl leading-tight">{p}</li>
                      ))}
                    </ul>
                  </div>
                  {cat.suitable && (
                    <div>
                      <div className="eyebrow text-foreground/55 mb-4">{cat.suitableLabel}</div>
                      <ul className="space-y-2.5">
                        {cat.suitable.map((s) => (
                          <li key={s} className="display-serif text-xl sm:text-2xl leading-tight">{s}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CUSTOM SAUCE DEVELOPMENT */}
      <section className="bg-[#F5EFE6]" data-testid="custom-section">
        <div className="container-pad py-24 sm:py-32">
          <div className="grid lg:grid-cols-12 gap-16">
            <div className="lg:col-span-5">
              <div className="eyebrow text-[#F97316]">— Bespoke</div>
              <h2 className="display-serif mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                {t.b2b.customTitle}
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-xl sm:text-2xl font-light leading-[1.6] text-foreground/75">
                {t.b2b.customBody}
              </p>
              <Link to="/contact" data-testid="custom-cta" className="mt-10 inline-flex items-center btn-outline">
                {t.b2b.cta} <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY WORK WITH GARNACHA */}
      <section data-testid="b2b-benefits-section">
        <div className="container-pad py-24 sm:py-32">
          <div className="eyebrow text-[#F97316] mb-12">{t.b2b.benefits}</div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-16 gap-y-16">
            {t.b2b.b.map((item, i) => (
              <div key={i} data-testid={`b2b-benefit-${i}`}>
                <h3 className="display-serif text-2xl sm:text-3xl leading-tight">{item.t}</h3>
                <div className="mt-5 w-10 h-px bg-[#F97316]" />
                <p className="mt-5 text-foreground/65 font-light text-base leading-relaxed">{item.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA + FORM */}
      <section className="bg-[#222] text-[#FFF8F0]" data-testid="b2b-cta-section">
        <div className="container-pad py-24 sm:py-32 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <h2 className="display-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.02]">
              {t.b2b.ctaHeading}
            </h2>
            <p className="mt-8 text-[#FFF8F0]/70 font-light max-w-md text-lg leading-relaxed">
              Send us a note about your concept, format needs or a custom recipe brief. We&rsquo;ll reply within 2 business days.
            </p>
          </div>
          <form onSubmit={onSubmit} className="lg:col-span-6 lg:col-start-7 space-y-8" data-testid="b2b-form">
            <input data-testid="b2b-name" required placeholder={t.contact.name} className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316]" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            <input data-testid="b2b-company" required placeholder={t.contact.company} className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316]" value={form.company} onChange={(e) => setForm({...form, company: e.target.value})} />
            <input data-testid="b2b-email" type="email" required placeholder={t.contact.email} className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316]" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
            <input data-testid="b2b-phone" placeholder={t.contact.phone} className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316]" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} />
            <input data-testid="b2b-businesstype" placeholder="Business type (restaurant, food truck, catering...)" className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316]" value={form.business_type} onChange={(e) => setForm({...form, business_type: e.target.value})} />
            <textarea data-testid="b2b-message" rows={4} placeholder={t.contact.message} className="input-clean text-[#FFF8F0] border-white/40 placeholder:text-[#FFF8F0]/55 focus:border-[#F97316] resize-none" value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} />
            <button data-testid="b2b-submit-btn" type="submit" disabled={submitting} className="btn-primary text-base px-10 py-4 disabled:opacity-60 shadow-[0_8px_24px_-8px_rgba(249,115,22,0.55)]">
              {submitting ? "..." : t.b2b.cta}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default B2B;
